import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ExpressionConfiguration } from "../../../../../rule-engine-core/Expressions/ExpressionConfiguration";
import { StringEqualExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { ValueTableConfiguration } from "../../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { TAXONOMY_MAPPINGS } from "../../../data/generatorConfig";
import type { EnrichedPart, TaxonomyProductMapping } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import type { AppGlueStorageBackedConfig } from "../../ruleIntegrationTypes";
import { createRuleSummaryPresentation } from "../../ruleIntegrationTypes";
import type { CreateValueTableIntegrationAdapterParams } from "../../executableFamilies/valueTable/ValueTableIntegrationAdapter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";

export const TR005_RULE_ID = "TR-005";
export const TR005_APPGLUE_EXECUTABLE_TYPE = "ValueTableConfiguration";

type Tr005ValueTableResult = {
    status: DemoRuleStatus;
    output_queue: DemoOutputQueue;
    reason_code: string;
    explanation: string;
    taxonomy_path: string;
    erp_product_group: string;
    erp_product_line: string;
    crm_family: string;
};

const RESULT_COLUMN_NAMES = [
    "status",
    "output_queue",
    "reason_code",
    "explanation",
    "taxonomy_path",
    "erp_product_group",
    "erp_product_line",
    "crm_family",
] as const;

const createInputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    inputData.fields.push(new StringDataDefinition("taxonomy_path"));
    return inputData;
};

const createResultDefinition = (): DataDefinition => {
    const resultData = new DataDefinition();
    RESULT_COLUMN_NAMES.forEach((name) => resultData.fields.push(new StringDataDefinition(name)));
    return resultData;
};

const taxonomyEqualsCellValue = (): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = "cellValue";
    expression.value2.variableName = "taxonomy_path";
    return expression;
};

const expressionConfig = (expression: StringEqualExpression): ExpressionConfiguration => {
    const config = new ExpressionConfiguration();
    config.expression = expression;
    return config;
};

export const buildTr005ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData({
        taxonomy_path: part.taxonomyPath,
    });

const getResultColumnId = (config: ValueTableConfiguration, name: (typeof RESULT_COLUMN_NAMES)[number]): string => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) throw new Error(`TR-005 value table is missing result column ${name}.`);
    return column._id;
};

const setResultCellValue = (
    config: ValueTableConfiguration,
    rowIndex: number,
    name: (typeof RESULT_COLUMN_NAMES)[number],
    value: string,
) => {
    config.rows[rowIndex]!.resultCellsData[getResultColumnId(config, name)].value = value;
};

const getResultCellValue = (
    config: ValueTableConfiguration,
    rowIndex: number,
    name: (typeof RESULT_COLUMN_NAMES)[number],
): unknown => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) return undefined;
    return config.rows[rowIndex]?.resultCellsData[column._id]?.value;
};

const getDefaultResultCellValue = (
    config: ValueTableConfiguration,
    name: (typeof RESULT_COLUMN_NAMES)[number],
): unknown => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) return undefined;
    return config.defaultResult[column._id]?.value;
};

const mappedResult = (mapping: TaxonomyProductMapping): Tr005ValueTableResult => ({
    status: "pass",
    output_queue: "published_catalog_candidates",
    reason_code: "TAXONOMY_MAPPED",
    explanation: "Source taxonomy has a configured ERP product group and CRM family mapping.",
    taxonomy_path: mapping.taxonomyPath,
    erp_product_group: mapping.erpProductGroup,
    erp_product_line: mapping.erpProductLine,
    crm_family: mapping.crmFamily,
});

const defaultResult = (): Tr005ValueTableResult => ({
    status: "flagged",
    output_queue: "pending_review",
    reason_code: "MISSING_TAXONOMY_MAPPING",
    explanation: "Source taxonomy is not mapped yet, so the row waits for product operations review.",
    taxonomy_path: "unmapped",
    erp_product_group: "unmapped",
    erp_product_line: "unmapped",
    crm_family: "unmapped",
});

const setRowResult = (config: ValueTableConfiguration, rowIndex: number, result: Tr005ValueTableResult) => {
    RESULT_COLUMN_NAMES.forEach((name) => setResultCellValue(config, rowIndex, name, result[name]));
};

const setDefaultResult = (config: ValueTableConfiguration) => {
    const result = defaultResult();
    RESULT_COLUMN_NAMES.forEach((name) => {
        config.defaultResult[getResultColumnId(config, name)].value = result[name];
    });
};

export const createDefaultTr005TaxonomyMappings = (): TaxonomyProductMapping[] =>
    TAXONOMY_MAPPINGS.map((mapping) => ({ ...mapping }));

export const createTr005TaxonomyValueTableConfiguration = (
    mappings: TaxonomyProductMapping[] = createDefaultTr005TaxonomyMappings(),
): ValueTableConfiguration => {
    AppGlueCoreInit.init();

    const config = new ValueTableConfiguration();
    config.title = "TR-005 taxonomy to downstream product mapping";
    config.inputData = createInputDefinition();
    config.executionResult = createResultDefinition();

    const taxonomyColumn = config.columns[0]!;
    taxonomyColumn.columnName = "taxonomy_path";
    taxonomyColumn.cellData = new StringDataDefinition("cellValue");
    taxonomyColumn.expression = expressionConfig(taxonomyEqualsCellValue());

    while (config.rows.length > 0) {
        config.removeRow(config.rows[0]!);
    }

    mappings.forEach((mapping, index) => {
        const row = config.addRow();
        row.cellData[taxonomyColumn._id].values = mapping.taxonomyPath;
        setRowResult(config, index, mappedResult(mapping));
    });

    setDefaultResult(config);
    return config;
};

const isStatus = (value: unknown): value is DemoRuleStatus =>
    value === "pass" || value === "flagged" || value === "rejected" || value === "skipped";

const isOutputQueue = (value: unknown): value is DemoOutputQueue =>
    value === "published_catalog_candidates" || value === "pending_review";

const asString = (value: unknown, fallback: string): string => (typeof value === "string" ? value : fallback);

const mappedResultPlaceholder = "Source taxonomy has a configured ERP product group and CRM family mapping.";

const normalizeValueTableResult = (result: unknown): Tr005ValueTableResult => {
    const data = result && typeof result === "object" ? (result as Record<string, unknown>) : {};
    const status = isStatus(data.status) ? data.status : "flagged";

    return {
        status,
        output_queue: isOutputQueue(data.output_queue)
            ? data.output_queue
            : status === "pass"
              ? "published_catalog_candidates"
              : "pending_review",
        reason_code: asString(data.reason_code, status === "pass" ? "TAXONOMY_MAPPED" : "MISSING_TAXONOMY_MAPPING"),
        explanation: asString(
            data.explanation,
            status === "pass" ? mappedResultPlaceholder : defaultResult().explanation,
        ),
        taxonomy_path: asString(data.taxonomy_path, "unmapped"),
        erp_product_group: asString(data.erp_product_group, "unmapped"),
        erp_product_line: asString(data.erp_product_line, "unmapped"),
        crm_family: asString(data.crm_family, "unmapped"),
    };
};

const formatRoutingSummary = (status: unknown, outputQueue: unknown, fallbackStatus: DemoRuleStatus): string => {
    const normalizedStatus = isStatus(status) ? status : fallbackStatus;
    const normalizedQueue = isOutputQueue(outputQueue)
        ? outputQueue
        : normalizedStatus === "pass"
          ? "published_catalog_candidates"
          : "pending_review";

    return `${normalizedStatus} -> ${normalizedQueue}`;
};

const renderTr005ActionLine = (config: ValueTableConfiguration): string => {
    const mappedRouting = config.rows.length
        ? formatRoutingSummary(getResultCellValue(config, 0, "status"), getResultCellValue(config, 0, "output_queue"), "pass")
        : "pass -> published_catalog_candidates";
    const defaultRouting = formatRoutingSummary(
        getDefaultResultCellValue(config, "status"),
        getDefaultResultCellValue(config, "output_queue"),
        "flagged",
    );

    return `ACTION mapped ${mappedRouting}; unmatched ${defaultRouting}`;
};

export const evaluateTr005TaxonomyMappingResult = (
    config: ValueTableConfiguration,
    part: EnrichedPart,
): Tr005ValueTableResult => normalizeValueTableResult(config.run(buildTr005ExecutionData(part), undefined));

export const getTr005TaxonomyMappingsFromValueTable = (config: ValueTableConfiguration): TaxonomyProductMapping[] =>
    config.rows.map((row, rowIndex) => ({
        taxonomyPath: String(row.cellData[config.columns[0]!._id].values ?? ""),
        erpProductGroup: asString(getResultCellValue(config, rowIndex, "erp_product_group"), ""),
        erpProductLine: asString(getResultCellValue(config, rowIndex, "erp_product_line"), ""),
        crmFamily: asString(getResultCellValue(config, rowIndex, "crm_family"), ""),
    }));

export const getTr005TaxonomyMappingsFromConfig = (
    config: AppGlueStorageBackedConfig | null,
): TaxonomyProductMapping[] | null =>
    config instanceof ValueTableConfiguration ? getTr005TaxonomyMappingsFromValueTable(config) : null;

export const updateTr005TaxonomyValueTableMapping = (
    config: ValueTableConfiguration,
    taxonomyPath: string,
    patch: Partial<Pick<TaxonomyProductMapping, "erpProductGroup" | "erpProductLine" | "crmFamily">>,
): ValueTableConfiguration => {
    const nextMappings = getTr005TaxonomyMappingsFromValueTable(config).map((mapping) =>
        mapping.taxonomyPath === taxonomyPath
            ? {
                  ...mapping,
                  erpProductGroup: patch.erpProductGroup ?? mapping.erpProductGroup,
                  erpProductLine: patch.erpProductLine ?? mapping.erpProductLine,
                  crmFamily: patch.crmFamily ?? mapping.crmFamily,
              }
            : mapping,
    );

    return createTr005TaxonomyValueTableConfiguration(nextMappings);
};

const renderTr005Summary = (config: ValueTableConfiguration) => {
    const mappings = getTr005TaxonomyMappingsFromValueTable(config);
    const samples = mappings
        .slice(0, 3)
        .map((mapping) => `${mapping.taxonomyPath} -> ${mapping.erpProductGroup}`)
        .join(", ");

    return createRuleSummaryPresentation({
        status: "complete",
        lines: [
            `LOOKUP ${mappings.length} taxonomy mapping rows`,
            samples ? `MAPPINGS ${samples}` : "MAPPINGS stored in AppGlue value table",
            renderTr005ActionLine(config),
        ],
        sources: ["native_appglue_config", "adapter_projection"],
    });
};

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-TR005-SAMPLE",
    mpn: "TI-TR005",
    manufacturerId: "MFR-001",
    manufacturerName: "Texas Instruments",
    category: "Semiconductor",
    taxonomyPath: "Semiconductor > Microcontroller > 32-bit",
    packageType: "QFN",
    pinCount: 32,
    voltageMin: 1.8,
    voltageMax: 3.3,
    capacitancePf: null,
    resistanceOhms: null,
    tolerancePct: null,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 92,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

export const TR005_VALUE_TABLE_POLICY: CreateValueTableIntegrationAdapterParams<EnrichedPart> = {
    ruleId: TR005_RULE_ID,
    policyBinding: requireRulePolicyBinding(TR005_RULE_ID),
    createConfig: (context = {}) =>
        createTr005TaxonomyValueTableConfiguration(
            context.taxonomyMappings ?? context.data?.taxonomyMappings ?? createDefaultTr005TaxonomyMappings(),
        ),
    evaluator: {
        evaluate: (config, part) => {
            const result = evaluateTr005TaxonomyMappingResult(config, part);

            return {
                passed: result.status === "pass",
                status: result.status,
                reasonCode: result.reason_code,
                explanation: result.explanation,
                outputQueue: result.output_queue,
                comparisons: [
                    {
                        label: "Taxonomy",
                        expected: "configured mapping",
                        actual: result.erp_product_group,
                        ok: result.status === "pass",
                    },
                    {
                        label: "CRM family",
                        expected: "mapped family",
                        actual: result.crm_family,
                        ok: result.status === "pass",
                    },
                ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: TR005_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence: {
                    ...result,
                    partId: part.partId,
                    requestedTaxonomyPath: part.taxonomyPath,
                    defaultResult: result.status !== "pass" ? normalizeValueTableResult(defaultResult()) : undefined,
                    defaultOutputQueue: asString(getDefaultResultCellValue(config, "output_queue"), "pending_review"),
                },
            };
        },
    },
    summaryPresenter: {
        renderSummary: renderTr005Summary,
    },
    demoDataProjection: {
        projectDemoData: (config) => ({
            taxonomyMappings: getTr005TaxonomyMappingsFromValueTable(config),
        }),
    },
};

export const TR005_RUNTIME_SAMPLES = {
    passingRow: samplePart(),
    routedRow: samplePart({
        partId: "PART-TR005-UNMAPPED",
        taxonomyPath: "Passive > Inductor > Power",
    }),
};
